import { create } from 'zustand';
import { Organization } from '../../../types/Organization';
import { ResponseData } from '../../../types/response/ResponseData';
import ResponseError from '../../../types/response/ResponseError';
import { WebHookCreatedData, WebHookTestResponse } from '../../../api/webhooks/webhooks';
import { Webhook } from '../../../types/webhooks/Webhook';
import {
    BulkFailedWebhookAction,
    FailedWebhook,
    FailedWebhooks,
} from '../../../types/webhooks/FailedWebhook';

export interface WebhooksStore {
    isSearching: boolean;
    isSearchingFailedWebhooks: boolean;
    searchResults: ResponseData<Webhook[]>;
    searchFailedWebhooksResults: ResponseData<FailedWebhook[]>;
    created?: WebHookCreatedData;
    isCreating: boolean;
    createdError?: ResponseError;
    searchError?: ResponseError;
    searchFailedWebhooksError?: ResponseError;
    patchError?: ResponseError;
    deleteError?: ResponseError;
    isTesting: boolean;
    tested?: WebHookTestResponse;
    testError?: ResponseError;
    bulkFailedWebhookProcessing: boolean;
    bulkError?: ResponseError;
    bulkSucceeded: boolean;
    selectedFailedWebhooks: FailedWebhook[];
    bulkAction: BulkFailedWebhookAction;
    moreFailedWebhooksResultsAvailable: boolean;
    searchStarted: () => void;
    searchSucceeded: (results: ResponseData<Webhook[]>) => void;
    searchFailed: (error: ResponseError) => void;
    creating: () => void;
    creatingSucceeded: (data: WebHookCreatedData) => void;
    creatingFailed: (error: ResponseError) => void;
    createdCleared: () => void;
    patching: () => void;
    patchFailed: (error: ResponseError) => void;
    deleting: () => void;
    deleteFailed: (error: ResponseError) => void;
    testing: () => void;
    testingSucceeded: (response: WebHookTestResponse) => void;
    testFailed: (error: ResponseError) => void;
    testedCleared: () => void;
    searchFailedWebhooksStarted: () => void;
    searchFailedWebhooksSucceeded: (results: ResponseData<FailedWebhooks>) => void;
    searchFailedWebhooksFailed: (error: ResponseError) => void;
    bulkFailedWebhookStarted: () => void;
    bulkFailedWebhookSucceeded: () => void;
    bulkFailedWebhookFailed: (error: ResponseError) => void;
    clearBulkFailedWebhook: () => void;
    setSelectedFailedWebhooks: (failedWebhooks: FailedWebhook[]) => void;
    setBulkAction: (action: BulkFailedWebhookAction) => void;
    reset: () => void;
}

export const initialState = {
    isSearching: false,
    isSearchingFailedWebhooks: false,
    isCreating: false,
    searchResults: {} as ResponseData<Webhook[]>,
    searchFailedWebhooksResults: {} as ResponseData<FailedWebhook[]>,
    created: undefined,
    createdError: undefined,
    searchError: undefined,
    searchFailedWebhooksError: undefined,
    patchError: undefined,
    deleteError: undefined,
    isTesting: false,
    tested: undefined,
    testError: undefined,
    bulkFailedWebhookProcessing: false,
    bulkError: undefined,
    bulkSucceeded: false,
    selectedFailedWebhooks: [],
    bulkAction: { action: '', failedWebhooks: [] },
    moreFailedWebhooksResultsAvailable: false,
};

export const useWebhooksStore = create<WebhooksStore>(set => ({
    ...initialState,

    searchStarted: () =>
        set({
            isSearching: true,
        }),
    searchSucceeded: (results: ResponseData<Webhook[]>) =>
        set({
            isSearching: false,
            searchResults: results,
        }),
    searchFailed: (error: ResponseError) =>
        set({
            isSearching: false,
            searchError: error,
        }),
    creating: () =>
        set({
            isCreating: true,
            createdError: undefined,
        }),
    creatingSucceeded: (data: WebHookCreatedData) =>
        set({
            created: data,
            isCreating: false,
        }),
    creatingFailed: (error: ResponseError) =>
        set({
            isCreating: false,
            createdError: error,
        }),
    createdCleared: () =>
        set({
            created: undefined,
            isCreating: false,
            createdError: undefined,
        }),
    patching: () =>
        set({
            patchError: undefined,
        }),
    patchFailed: (error: ResponseError) =>
        set({
            patchError: error,
        }),
    deleting: () =>
        set({
            deleteError: undefined,
        }),
    deleteFailed: (error: ResponseError) =>
        set({
            deleteError: error,
        }),
    testing: () =>
        set({
            isTesting: true,
            testError: undefined,
        }),
    testingSucceeded: (response: WebHookTestResponse) =>
        set({
            tested: response,
            isTesting: false,
        }),
    testFailed: (error: ResponseError) =>
        set({
            isTesting: false,
            testError: error,
        }),
    testedCleared: () =>
        set({
            tested: undefined,
            isTesting: false,
            testError: undefined,
        }),
    searchFailedWebhooksStarted: () =>
        set({
            isSearchingFailedWebhooks: true,
        }),
    searchFailedWebhooksSucceeded: (results: ResponseData<FailedWebhooks>) =>
        set({
            isSearchingFailedWebhooks: false,
            searchFailedWebhooksResults: {
                ...results,
                data: results.data.actions.sort((a, b) => b.action.timestamp - a.action.timestamp),
            },
            moreFailedWebhooksResultsAvailable: results.data.continuationToken.length > 0,
        }),
    searchFailedWebhooksFailed: (error: ResponseError) =>
        set({
            isSearchingFailedWebhooks: false,
            searchFailedWebhooksError: error,
        }),
    bulkFailedWebhookStarted: () =>
        set({
            bulkFailedWebhookProcessing: true,
        }),
    bulkFailedWebhookSucceeded: () =>
        set({
            bulkFailedWebhookProcessing: false,
            bulkSucceeded: true,
        }),
    bulkFailedWebhookFailed: (error: ResponseError) =>
        set({
            bulkFailedWebhookProcessing: false,
            bulkError: error,
        }),
    clearBulkFailedWebhook: () =>
        set({
            bulkError: undefined,
            bulkSucceeded: false,
        }),
    setSelectedFailedWebhooks: (failedWebhooks: FailedWebhook[]) =>
        set({
            selectedFailedWebhooks: failedWebhooks,
        }),
    setBulkAction: (action: BulkFailedWebhookAction) =>
        set({
            bulkAction: action,
        }),
    reset: () => set(initialState),
}));
